import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { DatePipe, LowerCasePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { Api } from '@services/api.service';
import { AuthService } from '@services/auth.service';
import { Utils } from '@services/utils.service';
import { ToastService } from '@services/toast.service';
import { IOrganizationCustomer } from '@common-interfaces/organization-customer';
import { ICustomerHistoryItem } from '@common-interfaces/organization-customer-views';
import { PageHeaderComponent } from '../../../../components/shared/page-header/page-header.component';
import { StatCardComponent } from '../../../../components/shared/stat-card/stat-card.component';
import { EmptyStateComponent } from '../../../../components/shared/empty-state/empty-state.component';
import { CustomerFormComponent, CustomerFormValue } from '../customer-form/customer-form.component';
import { CustomerHistoryComponent, CustomerHistoryRow } from '../customer-history/customer-history.component';
import { customerStats } from '@common-helpers/customer-stats';
import { debugLogger } from '@common-helpers/debug-logger';

interface VenueRow {
    clientId: string;
    name: string;
    bookings: number;
    lastVisit: Date | null;
    value: string;
}

@Component({
    selector: 'organisation-customer',
    imports: [
        DatePipe,
        LowerCasePipe,
        RouterLink,
        TranslatePipe,
        TabsModule,
        TagModule,
        ButtonModule,
        SkeletonModule,
        PageHeaderComponent,
        StatCardComponent,
        EmptyStateComponent,
        CustomerFormComponent,
        CustomerHistoryComponent
    ],
    templateUrl: './organisation-customer.component.html'
})
export class OrganisationCustomerComponent {
    private api = inject(Api);
    private auth = inject(AuthService);
    private toast = inject(ToastService);
    private translate = inject(TranslateService);
    private confirm = inject(ConfirmationService);
    utility = inject(Utils);

    cid = input<string>('');

    orgId = this.auth.organizationId;
    loading = signal(true);
    saving = signal(false);
    syncing = signal<string | null>(null);
    selectedTab = signal('history');

    customer = signal<IOrganizationCustomer | null>(null);
    private historyByVenue = signal<Record<string, ICustomerHistoryItem[]>>({});

    private venueNames = computed(() => new Map(this.auth.clientsInOrganization().map((c) => [String(c._id), c.name as string])));

    history = computed<CustomerHistoryRow[]>(() =>
        Object.entries(this.historyByVenue())
            .flatMap(([clientId, items]) => items.map((item) => ({ ...item, venueName: this.venueNames().get(clientId) ?? '' })))
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    );

    stats = computed(() => customerStats(this.history()));

    venues = computed<VenueRow[]>(() => {
        const c = this.customer();
        if (!c) {
            return [];
        }
        return Object.keys(c.clientCustomerIds ?? {}).map((clientId) => {
            const s = customerStats(this.historyByVenue()[clientId] ?? []);
            return {
                clientId,
                name: this.venueNames().get(clientId) ?? this.translate.instant('UNKNOWN_VENUE'),
                bookings: s.bookings,
                lastVisit: s.lastVisit,
                value: this.utility.currencyTotals(s.totals, 'lifetimeValue')
            };
        });
    });

    fullName = computed(() => {
        const c = this.customer();
        return c ? `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || c.email || '' : '';
    });
    initials = computed(() => {
        const c = this.customer();
        return ((c?.firstName?.[0] ?? '') + (c?.lastName?.[0] ?? '')).toUpperCase() || (c?.email?.[0] ?? '?').toUpperCase();
    });

    constructor() {
        effect(() => {
            const id = this.cid();
            const orgId = this.orgId();
            if (id && orgId) {
                untracked(() => this.load(id));
            }
        });
    }

    async saveProfile(value: CustomerFormValue): Promise<void> {
        this.saving.set(true);
        try {
            const res = await this.api.currentorganization().patch<IOrganizationCustomer>(`/customers/${this.cid()}`, { customer: value });
            if (res.success) {
                this.customer.set(res.data);
                this.toast.showSuccess(this.translate.instant('CUSTOMER_SAVED'));
            }
        } catch (error) {
            this.showError(error);
        } finally {
            this.saving.set(false);
        }
    }

    confirmSync(venue: VenueRow): void {
        this.confirm.confirm({
            header: this.translate.instant('APPLY_ORG_PROFILE_TITLE'),
            message: this.translate.instant('APPLY_ORG_PROFILE_TEXT', { venue: venue.name }),
            icon: 'ph ph-arrows-down-up',
            acceptLabel: this.translate.instant('APPLY'),
            rejectLabel: this.translate.instant('CANCEL'),
            rejectButtonStyleClass: 'p-button-text',
            accept: () => this.sync(venue)
        });
    }

    private async sync(venue: VenueRow): Promise<void> {
        this.syncing.set(venue.clientId);
        try {
            const res = await this.api.currentorganization().post(`/customers/${this.cid()}/venues/${venue.clientId}/sync-from-org`, {});
            if (res.success) {
                this.toast.showSuccess(this.translate.instant('ORG_PROFILE_APPLIED', { venue: venue.name }));
            }
        } catch (error) {
            this.showError(error);
        } finally {
            this.syncing.set(null);
        }
    }

    private async load(id: string): Promise<void> {
        this.loading.set(true);
        try {
            const res = await this.api
                .currentorganization()
                .get<{ customer: IOrganizationCustomer; history: Record<string, ICustomerHistoryItem[]> }>(`/customers/${id}`);
            if (res.success && res.data) {
                this.customer.set(res.data.customer);
                this.historyByVenue.set(res.data.history ?? {});
            }
        } catch (error) {
            debugLogger.error('Failed to load customer', error);
            this.customer.set(null);
        } finally {
            this.loading.set(false);
        }
    }

    private showError(error: unknown): void {
        const code = error instanceof HttpErrorResponse ? error.error?.error : null;
        const key = code ? `ERR_${code}` : 'ERR_GENERIC';
        const message = this.translate.instant(key);
        this.toast.showError(message === key ? this.translate.instant('ERR_GENERIC') : message);
        debugLogger.error('Customer request failed', error);
    }
}
